import React from 'react';
import moment from 'moment-timezone';

import CoachingConstants, { CoachingSessionDuration } from 'coaching/CoachingConstants';
import Grid from 'components/Grid';
import Heading from 'components/Heading';
import Row from 'components/Row';
import Select from 'components/Select';
import SelectBox from 'components/SelectBox';
import TimezoneHelper from 'lib/timezones';
import { dayHasNoSlots } from 'coaching/components/CoachingHomeView/BookRecurringSession';
import {
  RecurringCoachingSessionSlotGroupings,
  SlotsForDay,
} from 'coaching/lib/coachingSessionSlots';
import { CoachingSessionSlot } from 'coaching/containers/GetPossibleCoachingSessionsForCoachContainer';
import { DayOfWeek } from 'globalTypes';

export function DaySelectRow({
  groupedSlots,
  onSelectDay,
  selectedDay,
}: {
  groupedSlots: RecurringCoachingSessionSlotGroupings;
  onSelectDay: (day: DayOfWeek) => void;
  selectedDay?: DayOfWeek;
}) {
  const daysToDisplayAsTwoLetters = ['Thursday'];

  return (
    <SelectBoxGridRow columns={CoachingConstants.SORTED_DAYS_OF_WEEK.length} label="Day">
      {CoachingConstants.SORTED_DAYS_OF_WEEK.map(day => {
        const dayOfWeek = moment(day, 'dddd');
        const dayOfWeekStr = dayOfWeek.format('dddd');

        return (
          <SelectBox
            key={day}
            data-test={`day-select-box-${day.toString()}`}
            flush
            full
            disabled={dayHasNoSlots(day, groupedSlots)}
            onSelect={() => onSelectDay(day)}
            selected={selectedDay === day}
            label={
              daysToDisplayAsTwoLetters.includes(dayOfWeekStr)
                ? dayOfWeek.format('dd')
                : dayOfWeekStr.substring(0, 1)
            }
            textSize="xsmall"
          />
        );
      })}
    </SelectBoxGridRow>
  );
}

export function TimeSelectRow({
  slotsForSelectedDay,
  onSelectTime,
  selectedTime,
}: {
  slotsForSelectedDay: SlotsForDay;
  onSelectTime: (time: string) => void;
  selectedTime?: string;
}) {
  return (
    <SelectBoxGridRow columns={2} label={`Time (${TimezoneHelper.userTimezoneReadableShortcode})`}>
      {Object.keys(slotsForSelectedDay).map(time => (
        <SelectBox
          key={time}
          data-test={`time-select-box-${time}`}
          flush
          full
          onSelect={() => onSelectTime(time)}
          selected={selectedTime === time}
          label={time}
        />
      ))}
    </SelectBoxGridRow>
  );
}

export function SlotSelectRow({
  slotsForSelectedTime,
  onSelectSlot,
  selectedSlot,
}: {
  slotsForSelectedTime?: CoachingSessionSlot[];
  onSelectSlot: (slot: CoachingSessionSlot) => void;
  selectedSlot?: CoachingSessionSlot;
}) {
  return (
    <SelectBoxGridRow columns={2} label="Start date">
      {(slotsForSelectedTime ?? []).map(slot => (
        <SelectBox
          key={slot.startDateTime.toISOString()}
          data-test={`slot-select-box-${slot.startDateTime.toISOString()}`}
          flush
          full
          onSelect={() => onSelectSlot(slot)}
          selected={slot.startDateTime.isSame(selectedSlot?.startDateTime)}
          label={slot.startDateTime.format('ddd, MMM Do')}
        />
      ))}
    </SelectBoxGridRow>
  );
}

export function SlotSelect({
  slotsForSelectedTime,
  onSelectSlot,
  selectedSlot,
  disabled,
  placeholder,
}: {
  slotsForSelectedTime?: CoachingSessionSlot[];
  onSelectSlot: (isoStartDateTime: string) => void;
  selectedSlot?: CoachingSessionSlot;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <Row>
      <Row size="xsmall">
        <Heading level="5" flush>
          Start date
        </Heading>
      </Row>
      <Select
        data-test="session-slot-select"
        placeholder={placeholder}
        disabled={disabled}
        options={(slotsForSelectedTime ?? []).map(slot => {
          return {
            label: slot.startDateTime.format('ddd, MMM Do'),
            value: slot.startDateTime.toISOString(),
          };
        })}
        onChange={onSelectSlot}
        value={selectedSlot?.startDateTime.toISOString()}
      />
    </Row>
  );
}

export function DurationSelectRow({
  onSelectDuration,
  selectedDuration,
  durationOptions,
}: {
  onSelectDuration: (duration: CoachingSessionDuration) => void;
  selectedDuration?: CoachingSessionDuration;
  durationOptions: CoachingSessionDuration[];
}) {
  return (
    <SelectBoxGridRow columns={2} label="Length">
      {durationOptions.map(duration => (
        <SelectBox
          key={duration}
          data-test={`duration-select-box-${duration}`}
          flush
          full
          onSelect={() => onSelectDuration(duration)}
          selected={selectedDuration === duration}
          label={`${duration} minutes`}
          textSize="xsmall"
        />
      ))}
    </SelectBoxGridRow>
  );
}

export function SelectBoxGridRow({
  columns,
  label,
  children,
}: {
  columns: number;
  label: string;
  children: React.ReactElement<typeof SelectBox>[];
}) {
  return (
    <Row>
      <Row size="xsmall">
        <Heading level="5" flush>
          {label}
        </Heading>
      </Row>
      <Grid columns={columns} gap="xxsmall">
        {children}
      </Grid>
    </Row>
  );
}
